import { ReactElement, useCallback } from 'react';
import { App, Form } from 'antd';
import { styled } from 'styled-components';
import ContentLayout from './ContentLayout';
import ContentHeader from './content/ContentHeader';
import ContentBody from './content/ContentBody';
import ContentFooter from './content/ContentFooter';
import useCreateCustomers from '../hooks/useCreateCustomers';
import { BulkCreateCustomerItem } from '@minutes-system/backend/src/api/customers/schema';
import { useNavigate } from 'react-router-dom';
import { blockStyle } from '../../../../components/styles/Block';

const ContentBlock = styled.article`
  ${blockStyle}
  background: #fff;
  border-radius: 8px;
`;

interface CustomerFormValue {
  name: string;
  phone: string;
  mobilePhone: string;
  rowKey: number;
}

interface FormValues {
  customers: CustomerFormValue[];
}

const MIN_ROWS = 1;
const MAX_ROWS = 10;

const Content = (): ReactElement => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { results, createCustomers } = useCreateCustomers();
  const { message } = App.useApp();

  const onFinish = useCallback(
    async (values: FormValues) => {
      try {
        const body: BulkCreateCustomerItem[] = values.customers.map(customer => ({
          name: customer.name.trim(),
          phone: customer.phone || null,
          mobilePhone: customer.mobilePhone || null,
          rowKey: customer.rowKey,
        }));

        const response = await createCustomers(body);
        const { success, error } = response.summary;
        const total = success + error;

        if (error === 0) {
          message.success('全ての顧客が正常に登録されました');
        } else if (success === 0) {
          message.error('全ての顧客の登録に失敗しました。エラーがある行の詳細を確認し、必要に応じて修正してください。');
        } else {
          message.warning(
            `${total.toString()}件中${success.toString()}件が正常に登録されました。${error.toString()}件にエラーがあります。エラーがある行の詳細を確認し、必要に応じて修正してください。`
          );
        }
      } catch (error) {
        console.error('Failed to create customers:', error);
        message.error('顧客の登録に失敗しました');
      }
    },
    [createCustomers, message]
  );

  const handleCancel = useCallback(() => {
    void navigate('/customers');
  }, [navigate]);

  return (
    <ContentBlock>
      <Form<FormValues>
        form={form}
        onFinish={values => {
          void onFinish(values);
        }}
        initialValues={{
          customers: [{ name: '', phone: '', mobilePhone: '' }],
        }}
      >
        <Form.List name='customers'>
          {(fields, { add, remove }) => (
            <ContentLayout
              header={
                <ContentHeader
                  rowCount={fields.length}
                  onAdd={() => {
                    add({ name: '', phone: '', mobilePhone: '' });
                  }}
                  maxRows={MAX_ROWS}
                />
              }
              body={<ContentBody fields={fields} onRemove={remove} minRows={MIN_ROWS} results={results} />}
              footer={<ContentFooter total={fields.length} maxRows={MAX_ROWS} onCancel={handleCancel} />}
            />
          )}
        </Form.List>
      </Form>
    </ContentBlock>
  );
};

export default Content;
